import type { Company } from '@pn/core/domain/intel/company';
import type { StockInfo } from '@pn/core/domain/intel/intel';
import type { LandSaleDetails } from '@pn/core/domain/intel/land-sale';
import {
  companiesOverviewMapping,
  companyMonthlyStatsMapping,
  publicCompanyQuarterlyStatsMapping,
} from '@pn/resources/intel-stats-mappings';
import { apiDataItemMapper } from '@pn/services/api/data/apiDataItemMapper';
import { isEmpty, orderBy, reverse } from 'lodash-es';
import { apiIntelDataMapper } from '../intel-data/apiIntelDataMapper';
import type { ApiMergerAndAcquisition } from '../intel-data/types';
import { pnApiClient } from '../pnApiClient';
import { apiCompanyInfoMapper } from './apiCompanyInfoMapper';
import type {
  ApiBOEReportArticle,
  ApiCompanyDocument,
  ApiCompanyExecutive,
  ApiCompanyKPI,
  ApiDailyHistoricalStockPrices,
  ApiExtraCompanyInfo,
  ApiMineralRightsTransferDetail,
  ApiQuarterlyFinancialStatement,
} from './types';

const FMP_BASE_ENDPOINT = 'v1/market_intel/fmp';

const requestQuarterlyStatisticsByCompanyId = async (companyId: string) => {
  const apiData = await pnApiClient.request<any[]>({
    url: 'v1/market_intel/quarterly_stats/' + companyId,
  });

  const data = apiData.map(
    apiDataItemMapper(publicCompanyQuarterlyStatsMapping).toDomainDataItem
  );

  return orderBy(data, 'year_and_quarter', 'asc');
};

const requestQuarterlyStatisticsAggregation = async (
  aggregationType: 'sum' | 'avg' | 'median'
) => {
  const apiData = await pnApiClient.request<any[]>({
    url: 'v1/market_intel/quarterly_stats/aggregate/' + aggregationType,
  });

  const data = apiData.map(
    apiDataItemMapper(publicCompanyQuarterlyStatsMapping).toDomainDataItem
  );

  return orderBy(data, 'year_and_quarter', 'asc');
};

const requestGrossMonthlyStatisticsAggregation = async (
  aggregationType: 'sum' | 'avg' | 'median'
) => {
  const apiData = await pnApiClient.request<any[]>({
    url: '/v1/market_intel/licensed_production/aggregate/' + aggregationType,
  });

  const data = apiData.map(
    apiDataItemMapper(companyMonthlyStatsMapping).toDomainDataItem
  );

  return orderBy(data, 'date', 'asc');
};

const requestMonthlyStatisticsByCompanyIds = async (companyIds: string[]) => {
  const apiData = await pnApiClient.request<
    { company_id: string; productions: any[] }[]
  >({
    url: 'v1/market_intel/licensed_production?company=' + companyIds.join(','),
  });

  return apiData.map(
    ({
      company_id,
      productions,
    }: {
      company_id: string;
      productions: any[];
    }) => {
      return {
        companyId: company_id,
        dataItems: productions.map(
          apiDataItemMapper(companyMonthlyStatsMapping).toDomainDataItem
        ),
      };
    }
  );
};

const requestCompanyExtraInfo = async (
  companyStockSymbol: Company['stockSymbol']
) => {
  const apiCompanyIntelPromise = pnApiClient.request<ApiExtraCompanyInfo[]>({
    url: `${FMP_BASE_ENDPOINT}?endpoint=profile&symbol=${companyStockSymbol}`,
  });

  const apiCompanyExecutivesPromise = pnApiClient.request<
    ApiCompanyExecutive[]
  >({
    url: `${FMP_BASE_ENDPOINT}?endpoint=key_executives&symbol=${companyStockSymbol}`,
  });

  const apiStockPricesPromise = pnApiClient.request<{
    historical: ApiDailyHistoricalStockPrices[];
  }>({
    url: `${FMP_BASE_ENDPOINT}?endpoint=historical_price_full&symbol=${companyStockSymbol}&serietype=line`,
  });

  const apiFinancesPromise = pnApiClient.request<
    ApiQuarterlyFinancialStatement[]
  >({
    url: `${FMP_BASE_ENDPOINT}?endpoint=income_statement&symbol=${companyStockSymbol}&period=quarter&limit=${
      4 * 10
    }`,
  });

  const results = await Promise.all([
    apiCompanyIntelPromise,
    apiCompanyExecutivesPromise,
    apiStockPricesPromise,
    apiFinancesPromise,
  ]);

  return apiCompanyInfoMapper.toDomainCompanyInfo({
    ...results[0][0],
    executives: results[1],
    historical: reverse(results[2].historical),
    quarterlyFinancialStatements: results[3],
  });
};

const requestCompanyStockInformation = async (
  companyStockSymbol: Company['stockSymbol']
) => {
  const apiCompanyStockChange = await pnApiClient.request<
    StockInfo['changes'][]
  >({
    url: `${FMP_BASE_ENDPOINT}?endpoint=stock_price_change&symbol=${companyStockSymbol}`,
  });

  const apiCompanyRealTimeStockPrice = await pnApiClient.request<
    { price: number }[]
  >({
    url: `${FMP_BASE_ENDPOINT}?endpoint=quote_short&symbol=${companyStockSymbol}`,
  });

  const currentPrice = !isEmpty(apiCompanyRealTimeStockPrice)
    ? apiCompanyRealTimeStockPrice[0].price
    : 0;

  const changes = !isEmpty(apiCompanyStockChange[0])
    ? apiCompanyStockChange[0]
    : { '3Y': 0, '2. high': 0, '3. low': 0, '4. close': 0 };

  // should use a mapper but for the sake of time, I'm just going to return the data
  return {
    changes,
    price: currentPrice,
    time: new Date().toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
    }),
  };
};

const requestCompanyDocuments = async (companyId: Company['id']) => {
  const apiCompanyDocuments = await pnApiClient.request<ApiCompanyDocument[]>({
    url: `v1/market_intel/documents?company=${companyId}`,
  });
  return apiCompanyDocuments.map((apiCompanyDocument) => {
    return apiCompanyInfoMapper.toDomainCompanyDocument(apiCompanyDocument);
  });
};

const requestCompanyDocumentBlob = async (documentId: string) => {
  return pnApiClient.request<Blob>({
    url: 'v1/market_intel/documents/' + documentId + '/download',
  });
};

const requestCompanyReserves = async (companyId: Company['id']) => {
  return pnApiClient.request<any[]>({
    url: `v1/market_intel/reserves/${companyId}`,
  });
};

const requestCompanyBOEArticles = async (
  companyBOEArticleLink: Company['boeReportLink']
) => {
  const apiCompanyDocuments = await pnApiClient.request<ApiBOEReportArticle[]>({
    url: `v1/market_intel/boe_articles?company=${companyBOEArticleLink}`,
  });
  return apiCompanyDocuments.map((apiCompanyDocument) => {
    return apiCompanyInfoMapper.toDomainBOEReportArticle(apiCompanyDocument);
  });
};

const requestMineralRightsTransferDetails = async (id: string) => {
  const apiMineralRightsTransferDetails = await pnApiClient.request<
    ApiMineralRightsTransferDetail[]
  >({
    url: `v1/market_intel/mineral_rights_transfers/${id}`,
  });

  return apiMineralRightsTransferDetails.map((detail) => {
    return {
      agreementId: detail.agreement_id,
      fromDOI: detail.from_doi,
      toDOI: detail.to_doi,
      areaHa: detail.area_ha,
      province: detail.province,
      landTractList: detail.land_tract_list.map((tract) => {
        return {
          petroninjaLandId: tract.petroninja_land_id,
          tract: tract.tract,
          landId: tract.land_id,
          areaHa: tract.area_ha,
        };
      }),
    };
  });
};

const requestCompanyKPIs = async (companyId: Company['id']) => {
  const apiCompanyKPIs = await pnApiClient.request<ApiCompanyKPI[]>({
    url: 'v1/market_intel/company_kpis?company=' + companyId,
  });

  return apiCompanyKPIs.map((apiCompanyKPI) => {
    return apiCompanyInfoMapper.toDomainCompanyKPI(apiCompanyKPI);
  });
};

const requestCompaniesOverviewStats = async () => {
  const apiCompaniesOverview = await pnApiClient.request<any[]>({
    url: 'v1/market_intel/company_kpis/table_overview?min_boe_per_day=1',
  });
  return apiCompaniesOverview.map(
    apiDataItemMapper(companiesOverviewMapping).toDomainDataItem
  );
};

const requestCompanyLastClosingStockPrice = async (
  companyStockSymbol: Company['stockSymbol']
) => {
  const apiCompanyStockChange = await pnApiClient.request<
    | {
        symbol: string;
        historical: {
          close: number;
        }[];
      }
    | undefined
  >({
    url: `${FMP_BASE_ENDPOINT}?endpoint=historical_price_full&symbol=${companyStockSymbol}`,
  });

  return apiCompanyStockChange?.historical?.at(0)?.close ?? -1;
};

type ApiFMPApiEV = {
  symbol: string;
  date: string;
  stockPrice: number;
  numberOfShares: number;
  marketCapitalization: number;
  minusCashAndCashEquivalents: number;
  addTotalDebt: number;
  enterpriseValue: number;
};

const requestCompanyEnterpriseValue = async (
  companyStockSymbol: Company['stockSymbol']
) => {
  const apiCompanyStockChange = await pnApiClient.request<ApiFMPApiEV[]>({
    url: `${FMP_BASE_ENDPOINT}?endpoint=enterprise_values&symbol=${companyStockSymbol}&period=day&limit=1`,
  });

  return apiCompanyStockChange.at(0)?.enterpriseValue ?? -1;
};

const requestMergersAndAcquisitions = async () => {
  const apiMergersAndAcquisitions = await pnApiClient.request<
    ApiMergerAndAcquisition[]
  >({
    url: 'v1/market_intel/ma',
  });

  return apiMergersAndAcquisitions.map((apiWellLicenseTransfer) => {
    return apiIntelDataMapper.toDomainMergerAndAcquisition({
      ...apiWellLicenseTransfer,
    });
  });
};

const requestLandSaleOfferDates = async () => {
  const apiLandSaleOfferDate = await pnApiClient.request<
    { notice_date: string }[]
  >({
    url: 'v1/market_intel/land_sales/dates',
  });

  const dates = apiLandSaleOfferDate.reduce<string[]>((acc, curr) => {
    return [...acc, curr.notice_date];
  }, []);

  dates.sort((a, b) => {
    const dateA = new Date(a);
    const dateB = new Date(b);

    if (dateA > dateB) return 1;
    if (dateA < dateB) return -1;
    return 0;
  });
  return dates;
};

const requestLandSaleDetails = async (
  noticeDate: string,
  contractNumber: string
) => {
  return await pnApiClient.request<LandSaleDetails>({
    url:
      'v1/market_intel/land_sales/' +
      contractNumber +
      '/' +
      noticeDate +
      '/details',
  });
};

export const apiCompanyInfoProvider = {
  getCompanyKPIs: requestCompanyKPIs,
  getCompaniesOverviewStats: requestCompaniesOverviewStats,
  getQuarterlyStatisticsByCompanyId: requestQuarterlyStatisticsByCompanyId,
  getQuarterlyStatisticsAggregation: requestQuarterlyStatisticsAggregation,
  getGrossMonthlyStatisticsAggregation:
    requestGrossMonthlyStatisticsAggregation,
  getMonthlyStatisticsByCompanyIds: requestMonthlyStatisticsByCompanyIds,
  getCompanyExtraInfo: requestCompanyExtraInfo,
  getCompanyStockInformation: requestCompanyStockInformation,
  getCompanyLastClosingStockPrice: requestCompanyLastClosingStockPrice,
  getCompanyEnterpriseValue: requestCompanyEnterpriseValue,
  getCompanyDocuments: requestCompanyDocuments,
  getCompanyDocumentBlob: requestCompanyDocumentBlob,
  getCompanyReserves: requestCompanyReserves,
  getBOEReportArticles: requestCompanyBOEArticles,
  getMineralRightsTransferDetails: requestMineralRightsTransferDetails,
  getMergersAndAcquisitions: requestMergersAndAcquisitions,
  getLandSaleDetails: requestLandSaleDetails,
  getLandSaleOfferDates: requestLandSaleOfferDates,
};
